import { useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Search, AlertTriangle, ListOrdered, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { HELP_SECTIONS, HelpTopic } from './helpContent';

/**
 * Panel guidance for both portals.
 *
 * One page rather than a per-screen tooltip system: people come here with "how do I do X", not "what
 * is this button", and a single searchable list answers that in one place. Topics are filtered by
 * audience so a merchant is never shown a staff-only screen they cannot open — which would read as a
 * missing permission rather than a screen that was never theirs.
 */
export function HelpPage() {
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const sections = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const visible = (t: HelpTopic) => t.audience === 'both' || t.audience === (isMerchant ? 'merchant' : 'internal');
    const matches = (t: HelpTopic) => {
      if (!needle) return true;
      // Searches the whole topic, not just the title — people search for the words in the problem
      // ("weight dispute", "cod not paid"), which usually live in the body rather than the heading.
      const hay = [t.title, t.path, t.what, t.when, ...(t.how ?? []), ...(t.notes ?? [])].join(' ').toLowerCase();
      return hay.includes(needle);
    };
    return HELP_SECTIONS
      .map((s) => ({ ...s, topics: s.topics.filter((t) => visible(t) && matches(t)) }))
      .filter((s) => s.topics.length > 0);
  }, [q, isMerchant]);

  const total = sections.reduce((n, s) => n + s.topics.length, 0);
  const searching = !!q.trim();

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen size={22} className="text-primary" /> Help
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {isMerchant
            ? 'What each screen in your portal is for, and how to do the common jobs.'
            : 'What each screen in the panel is for, the process behind it, and how to do the common jobs.'}
        </p>
      </div>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search — try “weight”, “pickup”, “COD”, “invoice”…"
          className="w-full border border-slate-200 rounded-lg pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {searching && (
          <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={15} />
          </button>
        )}
      </div>

      {searching && (
        <div className="text-xs text-slate-500 mb-3">
          {total === 0 ? 'Nothing matched — try a shorter word.' : `${total} topic${total > 1 ? 's' : ''} matched.`}
        </div>
      )}

      <div className="space-y-5">
        {sections.map((s) => (
          <div key={s.name}>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">{s.name}</div>
            {s.blurb && !searching && <div className="text-xs text-slate-500 mb-2 -mt-1">{s.blurb}</div>}
            <div className="space-y-2">
              {s.topics.map((t) => {
                const key = `${s.name}:${t.title}`;
                // While searching everything is expanded — hiding the matched text behind a click is
                // the opposite of what a search is for.
                const isOpen = searching || open[key];
                return (
                  <div key={key} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                      className="w-full text-left px-4 py-3 flex items-start gap-2 hover:bg-slate-50/60">
                      {isOpen ? <ChevronDown size={16} className="text-slate-400 mt-0.5 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 mt-0.5 shrink-0" />}
                      <span className="flex-1">
                        <span className="font-medium text-slate-800">{t.title}</span>
                        {t.path && <span className="text-[11px] text-slate-400 ml-2">{t.path}</span>}
                        {!isOpen && <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2">{t.what}</span>}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-4 pl-10 space-y-3">
                        <p className="text-sm text-slate-700 leading-relaxed">{t.what}</p>

                        {t.when && (
                          <p className="text-sm text-slate-600"><span className="font-medium text-slate-700">When you use it: </span>{t.when}</p>
                        )}

                        {t.how && t.how.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
                              <ListOrdered size={13} className="text-primary" /> How to do it
                            </div>
                            <ol className="list-decimal ml-5 space-y-1 text-sm text-slate-700">
                              {t.how.map((step, i) => <li key={i} className="leading-relaxed">{step}</li>)}
                            </ol>
                          </div>
                        )}

                        {t.notes && t.notes.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1">
                              <AlertTriangle size={13} /> Worth knowing
                            </div>
                            <ul className="list-disc ml-4 space-y-1 text-[13px] text-amber-900">
                              {t.notes.map((n, i) => <li key={i} className="leading-relaxed">{n}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-slate-400 border-t border-slate-100 pt-4">
        Something here wrong or missing? Raise a Support Ticket and we will correct it.
      </div>
    </div>
  );
}
